import { Modal } from 'bootstrap';
import { BModal } from 'bootstrap-vue-next';
import { defineComponent, h, onMounted, onUnmounted, onUpdated, ref } from 'vue';

export interface AppStudioModalControls {
    show(): unknown;
    hide(force?: boolean): unknown;
    toggle(): unknown;
}

const registry = new Map<string, AppStudioModalControls>();
const hideInProgress = new Set<string>();
const hideFallbacks = new Map<string, ReturnType<typeof setTimeout>>();

function clearHide(id: string): void {
    hideInProgress.delete(id);
    const timeout = hideFallbacks.get(id);
    if (timeout) clearTimeout(timeout);
    hideFallbacks.delete(id);
}

function beginHide(id: string): void {
    hideInProgress.add(id);
    const existing = hideFallbacks.get(id);
    if (existing) clearTimeout(existing);
    hideFallbacks.set(id, setTimeout(() => clearHide(id), 1000));
}

function element(id: string): HTMLElement | null {
    return typeof document === 'undefined' ? null : document.getElementById(id);
}

export const appStudioModalService = {
    show(id: string): unknown {
        clearHide(id);
        const controls = registry.get(id);
        if (controls) return controls.show();
        const target = element(id);
        return target ? Modal.getOrCreateInstance(target).show() : undefined;
    },
    hide(id: string): unknown {
        const controls = registry.get(id);
        if (controls) {
            return controls.hide();
        }
        const target = element(id);
        return target ? Modal.getOrCreateInstance(target).hide() : undefined;
    }
};

function invoke(listener: unknown, args: unknown[]): void {
    if (Array.isArray(listener)) {
        listener.forEach(item => invoke(item, args));
    } else if (typeof listener === 'function') {
        listener(...args);
    }
}

export const AppStudioModal = defineComponent({
    name: 'AppStudioModal',
    inheritAttrs: false,
    props: {
        id: { type: String, required: true },
        hideFooter: { type: Boolean, default: false }
    },
    setup(props, { attrs, expose, slots }) {
        const modalRef = ref<InstanceType<typeof BModal>>();
        const localModelValue = ref(attrs.modelValue === true || attrs.visible === true || attrs.show === true);

        function modelValue(): boolean {
            return attrs.modelValue === undefined ? localModelValue.value : attrs.modelValue === true;
        }

        function setModelValue(value: boolean): void {
            localModelValue.value = value;
            invoke(attrs['onUpdate:modelValue'], [value]);
        }

        const controls: AppStudioModalControls = {
            show: () => setModelValue(true),
            hide: (force = false) => {
                if (!force && hideInProgress.has(props.id)) return undefined;
                setModelValue(false);
                return undefined;
            },
            toggle: () => setModelValue(!modelValue())
        };

        function register(): void {
            registry.set(props.id, controls);
        }

        onMounted(register);
        onUpdated(register);
        onUnmounted(() => {
            registry.delete(props.id);
            clearHide(props.id);
        });
        expose(controls);

        return () => h(BModal, {
            ...attrs,
            id: props.id,
            ref: modalRef,
            modelValue: modelValue(),
            noFooter: props.hideFooter || attrs.noFooter === true || attrs.noFooter === '',
            'onUpdate:modelValue': setModelValue,
            onHide: (...args: unknown[]) => {
                if (!hideInProgress.has(props.id)) {
                    beginHide(props.id);
                    invoke(attrs.onHide, args);
                }
            },
            onHidden: (...args: unknown[]) => {
                try {
                    invoke(attrs.onHidden, args);
                } finally {
                    Promise.resolve().then(() => clearHide(props.id));
                }
            },
            onHidePrevented: (...args: unknown[]) => {
                clearHide(props.id);
                invoke(attrs.onHidePrevented, args);
            },
            onShown: (...args: unknown[]) => {
                clearHide(props.id);
                invoke(attrs.onShown, args);
            }
        }, slots);
    }
});

export const appStudioModalRegistryForTests = {
    clear(): void {
        registry.clear();
        hideInProgress.clear();
        hideFallbacks.forEach(timeout => clearTimeout(timeout));
        hideFallbacks.clear();
    },
    has: (id: string): boolean => registry.has(id)
};
